import React, {
  useMemo,
  Fragment,
  useEffect,
} from "react";
import {
  MaterialReactTable,
  MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
} from "material-react-table";
import Typography from "@mui/material/Typography";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useViewClientSubAccountListMutation } from "../../../../reactQueries/AppApi/rqHooksForClientSubAccount";
import { dateformatConverter } from "../../../../common/utilities/dates/dateformatConverter";
import {
  useGetAccountAdminListQuery,
  useTrackActivityMutation,
} from "../../../../reactQueries/commonRQHooks";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import {
  clientSubAccountCreatePath,
  clientSubAccountEditPath,
} from "../../../../routes/routePaths";
import NoTableDataComp from "../../../../common/otherCommonComponents/NoTableDataComp";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import HtmlTooltip from "../../../../common/tooltips/HtmlTooltip";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import ExportButton from "../../../../common/otherCommonComponents/ExportButton";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const AuditDateCell = ({ renderedCellValue }) =>
  renderedCellValue
    ? dateformatConverter(renderedCellValue, "DD MMM YYYY hh:mm A")
    : "";

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || "";
};

const getClientSubAccountName = (row) => row?.ClientAccountName || "";

const getParentAccountName = (row) => row.ParentAccountName || "";

const getClientAccountCodes = (row) =>
  (row?.ClientAccountCodes || []).map((item) => item.clientCode).toString();

const createAdminEmailsAccessor = (accountAdmins) => (row) => {
  const emailList = [];
  (row?.ClientAccountAdmins || []).forEach(({ adminId }) => {
    const foundAdmin = accountAdmins?.find(
      (admin) => admin.adminId === adminId
    );
    if (foundAdmin) emailList.push(foundAdmin.emailAddress);
  });
  return emailList.toString() || "";
};

const getAccountStatus = (row) => (row?.IsActive ? "Active" : "Inactive");

const getCreatedBy = (row) => row?.CreatedBy || "";

const getCreatedDate = (row) =>
  row?.CreatedTimestamp ? new Date(row.CreatedTimestamp) : null;

const getUpdatedBy = (row) => row?.UpdatedBy || "";

const getUpdatedDate = (row) => {
  if (!row?.UpdatedBy) return null;
  return row?.UpdatedTimestamp ? new Date(row.UpdatedTimestamp) : null;
};

const ViewClientSubAccount = () => {
  const { refClientAccountCmsData } = useOutletContext();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const {
    clientPortalAdminRoleId,
    isEnterpriseAdmin,
    id: userId,
  } = loggedInUser;
  const { mutate: trackActivityMutate } = useTrackActivityMutation();
  const { isEnterpriseAdmin: isEnterpriseAdminType, clientPortalAdminType } = getAccessTypes()
  const {
    hasA1Access,
  } = clientPortalAdminType

  const canExportXls = isEnterpriseAdminType || hasA1Access;

  const {
    mutate: getClientSubAccountList,
    isSuccess,
    data: clientSubAccountData,
    isError,
    isLoading: isLoadingOnClientSubAccData,
  } = useViewClientSubAccountListMutation();

  useEffect(() => {
    getClientSubAccountList({
      loggedInUserId: userId,
      roleId: clientPortalAdminRoleId,
      isEnterpriseAdmin,
    });
  }, [loggedInUser]);

  const { data: accountAdminListFromAPI } = useGetAccountAdminListQuery();

  const fetchClientSubAccountCmsData = (key) => {
    const labels =
      refClientAccountCmsData?.Components?.ClientSubAccountGroupsList?.Labels;
    return returnLabel(key, labels);
  };

  const fetchCommonComponentCMSLabel = (key) => {
    const labels = refClientAccountCmsData?.Components?.CommonComponent?.Labels;
    return returnLabel(key, labels);
  };

  const clientSubAccountsTitle = fetchClientSubAccountCmsData('ClientSubAccountGroupsList.ClientSubAccountGroupTitle');
  const exportFileName = `${clientSubAccountsTitle}.xlsx`;
  const exportWorksheetName = clientSubAccountsTitle;

  const exportColumns = [
    { id: 'clientSubAccountName', header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.ClientSubAccountTableHeader"), getValue: (row) => row?.ClientAccountName || '' },
    { id: 'parentAccountName', header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.ClientAccount"), getValue: (row) => row?.ParentAccountName || '' },
    { id: 'clientAccountCodes', header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.ClientCodes"), getValue: (row) => (row?.ClientAccountCodes || []).map((item) => item.clientCode).filter(Boolean).join(', ') },
    {
      id: 'clientAccountAdmins',
      header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.Admins"),
      getValue: (row) => (row?.ClientAccountAdmins || [])
        .map(({ adminId }) => accountAdminListFromAPI?.find(
          (admin) => admin.adminId === adminId
        )?.emailAddress)
        .filter(Boolean)
        .join(', ')
    },
    { id: 'isActive', header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.Status"), getValue: (row) => row?.IsActive ? 'Active' : 'Inactive' },
    { id: 'created_by', header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.CreatedBy") || "Created By", getValue: (row) => row?.CreatedBy || '' },
    { id: 'created_date', header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.CreatedDate") || "Created Date", getValue: (row) => row?.CreatedTimestamp ? dateformatConverter(row.CreatedTimestamp, "DD MMM YYYY hh:mm A") : '' },
    { id: 'edited_by', header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.EditedBy") || "Edited By", getValue: (row) => {
        const updatedBy = row?.UpdatedBy;
        return updatedBy || '';
      }
    },
    { id: 'edited_on', header: fetchClientSubAccountCmsData("ClientSubAccountGroupsList.EditedOn") || "Edited On", getValue: (row) => {
        const updatedBy = row?.UpdatedBy;
        if (!updatedBy) return '';
        return row?.UpdatedTimestamp ? dateformatConverter(row.UpdatedTimestamp, "DD MMM YYYY hh:mm A") : '';
      }
    },
  ];

  const columns = useMemo(
    () => [
      {
        id: "clientSubAccountName",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.ClientSubAccountTableHeader"
        ),
        accessorFn: getClientSubAccountName,
        enablePinning: true,
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderClientSubAccount"
          ),
        },
      },
      {
        accessorFn: getParentAccountName,
        id: "parentAccountName",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.ClientAccount"
        ),
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderClientAccount"
          ),
        },
      },
      {
        id: "clientAccountCodes",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.ClientCodes"
        ),
        accessorFn: getClientAccountCodes,
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderClientCodes"
          ),
        },
      },
      {
        id: "clientAccountAdmins",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.Admins"
        ),
        accessorFn: createAdminEmailsAccessor(accountAdminListFromAPI),
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderAdmins"
          ),
        },
      },
      {
        accessorFn: getAccountStatus,
        id: "isActive",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.Status"
        ),
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderStatus"
          ),
        },
      },
      {
        id: "created_by",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.CreatedBy"
        ) || "Created By",
        accessorFn: getCreatedBy,
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderCreatedBy"
          ) || "Filter by Created By",
        },
      },
      //  Renamed from Published On → Created Date, removed old publishedOn column
      {
        accessorFn: getCreatedDate,
        id: "created_date",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.CreatedDate"
        ) || "Created Date",
        Cell: AuditDateCell,
        filterFn: "contains",
        sortingFn: "datetime",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderCreatedDate"
          ) || "Filter by Created Date",
        },
      },
      // ✅ Edited By — only shows value if record was edited
      // (UpdatedBy exists)
      {
        id: "edited_by",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.EditedBy"
        ) || "Edited By",
        accessorFn: getUpdatedBy,
        Cell: TooltipCell,
        filterFn: "contains",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderEditedBy"
          ) || "Filter by Edited By",
        },
      },
      // Edited On — only shows value if record was edited
      // (UpdatedBy exists)
      {
        id: "edited_on",
        header: fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.EditedOn"
        ) || "Edited On",
        accessorFn: getUpdatedDate,
        Cell: AuditDateCell,
        filterFn: "contains",
        sortingFn: "datetime",
        muiTableHeadCellFilterTextFieldProps: {
          placeholder: fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.PlaceholderEditedOn"
          ) || "Filter by Edited On",
        },
      },
    ],
    [accountAdminListFromAPI, refClientAccountCmsData]
  );

  let navigate = useNavigate();

  return (
    <Fragment>
      {!!isLoadingOnClientSubAccData &&
        fetchCommonComponentCMSLabel("Common.Waitloading")}

      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {fetchClientSubAccountCmsData(
          "ClientSubAccountGroupsList.ClientSubAccountGroupTitle"
        )}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={clientSubAccountCreatePath}
        >
          {fetchClientSubAccountCmsData(
            "ClientSubAccountGroupsList.SetupClientSubAccountGroup"
          )}
        </Button>
      </Stack>

      {!!isSuccess && !clientSubAccountData?.data?.length && (
        <NoTableDataComp />
      )}

      {!!isSuccess && !!clientSubAccountData?.data?.length && (
        <MaterialReactTable
          columns={columns}
          data={clientSubAccountData?.data || []}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}
          initialState={{
            density: "compact",
            showColumnFilters: true,
          }}
          state={{
            isLoading: isLoadingOnClientSubAccData,
            columnPinning: {
              left: ["mrt-row-actions", "clientSubAccountName"],
            },
          }}
          enableStickyHeader
          enableRowActions
          enablePinning
          renderToolbarInternalActions={({ table }) => (
            <>
              {canExportXls && (
                <ExportButton
                  table={table}
                  tooltipTitle={fetchClientSubAccountCmsData("ClientSubAccountGroupsList.ExportToXLS")}
                  trackActivity={trackActivityMutate}
                  userId={userId}
                  exportColumns={exportColumns}
                  fileName={exportFileName}
                  worksheetName={exportWorksheetName}
                />
              )}
              <MRTToggleFiltersButton table={table} />
              <MRTShowHideColumnsButton table={table} />
              <MRTFullScreenToggleButton table={table} />
            </>
          )}
          muiTablePaginationProps={{
            labelRowsPerPage:
              fetchCommonComponentCMSLabel("Common.Rowsperpage"),
          }}
          displayColumnDefOptions={{
            "mrt-row-actions": {
              header: fetchClientSubAccountCmsData(
                "ClientSubAccountGroupsList.Actions"
              ),
            },
          }}
          renderRowActions={({ row, table }) => {
            const Id = row.original.Id;
            return (
              <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
                <IconButton
                  onClick={() => {
                    navigate(clientSubAccountEditPath({ id: Id }));
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            );
          }}
        />
      )}
      <MpgErrorDialog open={!!isError} />
    </Fragment>
  );
};

export default ViewClientSubAccount;
